import { useState, useEffect, useRef } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';

interface Supplier {
  id: string;
  name: string;
  taxId?: string;
  email?: string;
  phone?: string;
  sendOcEmail?: boolean;
  sendOcWhatsapp?: boolean;
  address?: string;
  contactPerson?: string;
  paymentTerms?: string;
  paymentMethod?: string;
  paymentDays?: number;
  active?: boolean;
}

export function Suppliers() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    taxId: '',
    email: '',
    phone: '',
    sendOcEmail: true,
    sendOcWhatsapp: true,
    address: '',
    contactPerson: '',
    paymentTerms: '',
    paymentMethod: 'transferencia',
    paymentDays: 0,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Supplier[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const token = localStorage.getItem('accessToken');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  // Validation helpers
  const PAYMENT_METHOD_LABEL: Record<string, string> = {
    efectivo: 'Efectivo', transferencia: 'Transferencia', cheque: 'Cheque', tarjeta: 'Tarjeta', otro: 'Otro',
  };
  const PAYMENT_DAYS_LABEL: Record<number, string> = {
    0: 'Contra entrega', 7: '1 semana', 15: '15 días', 30: '30 días', 45: '45 días', 60: '60 días', 90: '90 días',
  };
  function paymentConditionLabel(s: Supplier): string {
    const method = PAYMENT_METHOD_LABEL[s.paymentMethod || 'transferencia'] || 'Transferencia';
    const days = PAYMENT_DAYS_LABEL[Number(s.paymentDays ?? 0)] ?? ` ${s.paymentDays} días`;
    const legacy = s.paymentTerms ? ` (${s.paymentTerms})` : '';
    return `${method} · ${days}${legacy}`;
  }
  const validateForm = (values: typeof form): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    if (!values.name.trim()) errors.name = 'El nombre es requerido';
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = 'Email inválido';
    if (values.phone && !/^\+[1-9]\d{1,14}$/.test(values.phone.trim())) {
      errors.phone = 'Teléfono inválido. Usá formato E.164 con + y código de país (ej: +5491127043909)';
    }
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  // Load suppliers with debounced search
  useEffect(() => {
    const handler = setTimeout(() => loadSuppliers(), 300);
    return () => clearTimeout(handler);
  }, [search]);

  async function loadSuppliers() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/suppliers?q=${search}`, { headers });
      if (res.ok) {
        const d = await res.json();
        const list: Supplier[] = d.data || [];
        setSuppliers(list);
        // Update suggestions for autocomplete
        if (search.length >= 2) {
          setSuggestions(list);
          setShowSuggestions(true);
        } else {
          setShowSuggestions(false);
        }
      } else {
        const err = await res.json();
        throw new Error(err.error || 'Error al cargar proveedores');
      }
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setForm({
      name: '',
      taxId: '',
      email: '',
      phone: '',
      sendOcEmail: true,
      sendOcWhatsapp: true,
      address: '',
      contactPerson: '',
      paymentTerms: '',
      paymentMethod: 'transferencia',
      paymentDays: 0,
    });
    setEditingId(null);
    setFormErrors({});
    setSaveMessage(null);
  }

  function startEdit(s: Supplier) {
    setEditingId(s.id);
    setShowForm(true);
    setForm({
      name: s.name || '',
      taxId: s.taxId || '',
      email: s.email || '',
      phone: s.phone || '',
      sendOcEmail: s.sendOcEmail ?? true,
      sendOcWhatsapp: s.sendOcWhatsapp ?? true,
      address: s.address || '',
      contactPerson: s.contactPerson || '',
      paymentTerms: s.paymentTerms || '',
      paymentMethod: s.paymentMethod || 'transferencia',
      paymentDays: s.paymentDays ?? 0,
    });
    setFormErrors({});
    setSaveMessage(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const { isValid, errors } = validateForm(form);
    setFormErrors(errors);
    if (!isValid) {
      setSubmitting(false);
      return;
    }
    try {
      const body: Record<string, string | number | boolean | undefined> = {
        name: form.name,
        taxId: form.taxId || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        sendOcEmail: form.sendOcEmail,
        sendOcWhatsapp: form.sendOcWhatsapp,
        address: form.address || undefined,
        contactPerson: form.contactPerson || undefined,
        paymentTerms: form.paymentTerms || undefined,
        paymentMethod: form.paymentMethod,
        paymentDays: Number(form.paymentDays) >= 0 ? Number(form.paymentDays) : 0,
      };
      let res;
      if (editingId) {
        res = await fetch(`${API}/suppliers/${editingId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API}/suppliers`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al guardar');
      }
      setSaveMessage('Guardado con éxito');
      setTimeout(() => setSaveMessage(null), 3000);
      resetForm();
      loadSuppliers();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      const delHeaders = { ...headers } as Record<string, string>;
      delete delHeaders['Content-Type'];
      const res = await fetch(`${API}/suppliers/${id}`, { method: 'DELETE', headers: delHeaders });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al eliminar');
      }
      setConfirmDelete(null);
      loadSuppliers();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    }
  }

  async function handleToggleActive(s: Supplier) {
    try {
      const res = await fetch(`${API}/suppliers/${s.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ active: !s.active }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al cambiar estado');
      }
      loadSuppliers();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    }
  }

  // Handle keyboard shortcuts: Ctrl+Enter to submit when form is focused
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'Enter' && showForm && !submitting) {
        e.preventDefault();
        if (formRef.current) {
          formRef.current.requestSubmit();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showForm, submitting]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Proveedores</h1>
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium min-h-[44px] transition-colors"
        >
          {showForm && !editingId ? 'Cancelar' : '+ Nuevo Proveedor'}
        </button>
      </div>

      {/* Error / Save messages */}
      {error && (
        <div className="bg-red-50 text-red-700 dark:text-red-300 p-4 rounded-lg mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm font-medium">⚠️ {error}</span>
          <button
            onClick={loadSuppliers}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors min-h-[44px]"
          >
            Reintentar
          </button>
        </div>
      )}
      {saveMessage && (
        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded text-center">
          {saveMessage}
        </div>
      )}

      {/* Search with autocomplete */}
      <div className="relative mb-4">
        <input
          ref={searchRef}
          placeholder="🔍 Buscar por nombre o CUIT..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-4 py-2 mb-2 border rounded"
          aria-label="Buscar proveedores"
        />
        {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></span>}
        {showSuggestions && (
          <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
            {suggestions.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">No se encontraron resultados</div>
            ) : (
              suggestions.slice(0, 8).map(s => (
                <div
                  key={s.id}
                  onMouseDown={() => {
                    setSearch(s.name || s.taxId || '');
                    setShowSuggestions(false);
                  }}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <div className="font-medium">{s.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{s.taxId || '-'}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6"
        >
          <h2 className="text-lg font-bold mb-4">
            {editingId ? '✏️ Editar Proveedor' : '🚚 Nuevo Proveedor'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Nombre / Razón Social *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                required
              />
              {formErrors.name && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CUIT</label>
              <input
                type="text"
                value={form.taxId}
                onChange={e => setForm({ ...form, taxId: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="30-12345678-9"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="proveedor@email.com"
              />
              {formErrors.email && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.email}</p>
              )}
              <label className="flex items-center gap-2 text-xs mt-2 text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.sendOcEmail}
                  onChange={e => setForm({ ...form, sendOcEmail: e.target.checked })}
                  className="h-3.5 w-3.5"
                />
                Enviar OC por email
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Teléfono</label>
              <input
                type="text"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="+54 11..."
              />
              {formErrors.phone && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.phone}</p>
              )}
              <label className="flex items-center gap-2 text-xs mt-2 text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.sendOcWhatsapp}
                  onChange={e => setForm({ ...form, sendOcWhatsapp: e.target.checked })}
                  className="h-3.5 w-3.5"
                />
                Enviar OC por WhatsApp
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Persona de Contacto</label>
              <input
                type="text"
                value={form.contactPerson}
                onChange={e => setForm({ ...form, contactPerson: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Forma de pago</label>
              <select
                value={form.paymentMethod}
                onChange={e => setForm({ ...form, paymentMethod: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              >
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="cheque">Cheque</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Momento de pago</label>
              <select
                value={form.paymentDays}
                onChange={e => setForm({ ...form, paymentDays: Number(e.target.value) })}
                className="w-full px-3 py-2 border rounded"
              >
                <option value={0}>Contra entrega</option>
                <option value={7}>1 semana</option>
                <option value={15}>15 días</option>
                <option value={30}>30 días</option>
                <option value={45}>45 días</option>
                <option value={60}>60 días</option>
                <option value={90}>90 días</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Dirección</label>
              <input
                type="text"
                value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="Calle, número, ciudad"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-600 text-white rounded-lg font-medium min-h-[44px] transition-colors disabled:opacity-70"
            >
              {submitting ? 'Guardando...' : editingId ? 'Guardar Cambios' : 'Crear Proveedor'}
            </button>
            <button
              type="button"
              onClick={() => { resetForm(); setShowForm(false); }}
              disabled={submitting}
              className="px-6 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-300 text-gray-700 dark:text-gray-200 rounded-lg font-medium min-h-[44px] transition-colors disabled:opacity-70"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Table / Mobile cards */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {/* Mobile cards */}
        <div className="block sm:hidden">
          {suppliers.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
              No se encontraron proveedores. {!search && 'Hacé clic en "Nuevo Proveedor" para crear el primero.'}
            </div>
          ) : (
            suppliers.map(s => (
              <div key={s.id} className={`p-4 border-b last:border-b-0 ${!s.active ? 'opacity-50' : ''}`}>
                <div>
                  <button
                    onClick={() => startEdit(s)}
                    className="font-semibold text-gray-800 dark:text-gray-100 text-left hover:text-blue-600 dark:hover:text-blue-400 min-h-[44px]"
                  >{s.name}</button>
                  <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{s.contactPerson || '-'} · {s.phone || '-'}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{s.taxId || '-'} · {paymentConditionLabel(s)}</p>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <button
                    onClick={() => handleToggleActive(s)}
                    className={`px-3 py-1.5 rounded text-xs font-medium min-h-[44px] min-w-[44px] ${s.active ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'}`}
                  >
                    {s.active ? 'Activo' : 'Inactivo'}
                  </button>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(s)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">✏️ Editar</button>
                    {confirmDelete === s.id ? (
                      <>
                        <button onClick={() => handleDelete(s.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px]">✕ Confirmar</button>
                        <button onClick={() => setConfirmDelete(null)} className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium px-3 py-1.5 min-h-[44px]">Cancelar</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDelete(s.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">🗑️ Eliminar</button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {/* Desktop table */}
        <table className="hidden sm:table w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">CUIT</th>
              <th className="px-4 py-3 text-left">Contacto</th>
              <th className="px-4 py-3 text-left">Teléfono</th>
              <th className="px-4 py-3 text-left">Pago</th>
              <th className="px-4 py-3 text-center">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.id} className={`border-t ${!s.active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-medium">
                  <button
                    onClick={() => startEdit(s)}
                    className="text-left hover:text-blue-600 dark:hover:text-blue-400 min-h-[44px]"
                  >{s.name}</button>
                </td>
                <td className="px-4 py-3 font-mono text-sm">{s.taxId || '-'}</td>
                <td className="px-4 py-3 text-sm">{s.contactPerson || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{s.phone || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{paymentConditionLabel(s)}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleToggleActive(s)}
                    className={`px-3 py-1.5 rounded text-xs font-medium min-h-[44px] min-w-[44px] ${s.active ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'}`}
                  >
                    {s.active ? 'Activo' : 'Inactivo'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(s)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">
                    ✏️ Editar
                  </button>
                  {confirmDelete === s.id ? (
                    <>
                      <button
                        onClick={() => handleDelete(s.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px]"
                      >
                        ✕ Confirmar
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium px-3 py-1.5 min-h-[44px]"
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(s.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]"
                    >
                      🗑️ Eliminar
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {suppliers.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
                  No se encontraron proveedores. {!search && 'Hacé clic en "Nuevo Proveedor" para crear el primero.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      {suppliers.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
          {suppliers.length} proveedor{suppliers.length !== 1 ? 'es' : ''} •{' '}
          {suppliers.filter(s => s.active).length} activos •{' '}
          {suppliers.filter(s => !s.active).length} inactivos
        </div>
      )}
    </div>
  );
}